import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { useLazyLoadQuery } from 'react-relay';
import { DashboardFiltersSiteQuery } from '../../graphQLOperations/__generated__/DashboardFiltersSiteQuery.graphql';
import { getFiltersInformationSiteAdmin } from '../../graphQLOperations/Dashboard';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectedTeacherDashboardFilter,
  setTeacherDashboardFilter,
} from '../../redux/reducers/teacherDashboardFiltersSlice';
import { FilterOptionValues } from './TeacherDashboard';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import { Class } from '../../models/Class';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';

interface Props {
  setOrderedClasses: Dispatch<SetStateAction<{ id: string; name: string }[]>>;
  availableOptions: { id: string; name: string }[];
  selectedOptions: { id: string; name: string }[] | null;
  setSelectedOption: Dispatch<SetStateAction<{ id: string; name: string }[]>>;
  applyFilters: () => void;
  filterOption: FilterOptionValues;
}

export const ClassesAndRoadmapsDropdown: FC<Props> = ({
  availableOptions,
  selectedOptions,
  setSelectedOption,
  applyFilters,
  filterOption,
  setOrderedClasses,
}) => {
  const dispatch = useAppDispatch();
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const hiddenClassIds = useHiddenClassIds();
  const { classes } = useLazyLoadQuery<DashboardFiltersSiteQuery>(
    getFiltersInformationSiteAdmin,
    {
      input: {
        siteIds: teacherDashboardFilters.sites?.length ? teacherDashboardFilters.sites.map(st => st.id) : null,
        isActive: 'ACTIVE',
        siteIsActive: 'ACTIVE',
      },
    },
    { fetchPolicy: 'network-only' },
  );
  useEffect(() => {
    if (filterOption === FilterOptionValues.CLASSES) {
      const rawClasses = ((classes?.nodes || []).filter(Boolean) as Class[]).filter(cl => !hiddenClassIds.has(cl.id));
      const orderedClasses = [...rawClasses].sort((a, b) =>
        formatClassDisplayName(a).localeCompare(formatClassDisplayName(b)),
      );

      if (orderedClasses.length) {
        const mappedClasses = orderedClasses.map(cl => ({
          id: cl.id || '',
          name: formatClassDisplayName(cl),
        }));
        setOrderedClasses(mappedClasses);
        const first = mappedClasses[0];
        dispatch(
          setTeacherDashboardFilter({
            ...teacherDashboardFilters,
            classes: [{ id: first.id, name: first.name }],
          }),
        );
        setSelectedOption([{ id: first.id, name: first.name }]);
      } else {
        dispatch(
          setTeacherDashboardFilter({
            ...teacherDashboardFilters,
            classes: null,
          }),
        );
        setSelectedOption([]);
        setOrderedClasses([]);
      }
    }
  }, [classes?.nodes, filterOption, teacherDashboardFilters.sites, hiddenClassIds]);

  const appliedOptions =
    (filterOption === FilterOptionValues.ROADMAP
      ? teacherDashboardFilters.roadmaps
      : teacherDashboardFilters.classes) || [];
  const appliedIds = appliedOptions.map(op => op.id);
  const selectedIds = (selectedOptions || []).map(op => op.id);
  const hasPendingChanges =
    selectedIds.length !== appliedIds.length || selectedIds.some(id => !appliedIds.includes(id));

  // Closing the panel without applying discards the pending selection: the hint and the
  // "Apply filters" button live inside the panel, so a checkbox left checked after the
  // panel is dismissed would be the only visible state and it would disagree with the
  // widgets.
  const discardPendingSelection = () => setSelectedOption(appliedOptions.map(op => ({ id: op.id, name: op.name })));

  const appliedChipsTemplate = (option?: { id: string; name: string }) => {
    if (option && option.id !== selectedOptions?.[0]?.id) {
      return null;
    }

    if (!appliedOptions.length) {
      return ' ';
    }

    return (
      <>
        {appliedOptions.map(applied => (
          <div
            key={applied.id}
            className="p-multiselect-token rounded-xl p-2"
            style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
          >
            <span className="p-multiselect-token-label">{applied.name}</span>
          </div>
        ))}
      </>
    );
  };

  return (
    <MultiSelect
      key={filterOption}
      value={selectedOptions}
      className="w-full"
      optionLabel="name"
      options={availableOptions}
      onChange={e => setSelectedOption(e.target.value)}
      display="chip"
      filter
      selectedItemTemplate={appliedChipsTemplate}
      onHide={discardPendingSelection}
      panelFooterTemplate={
        <div>
          {hasPendingChanges && (
            <p className="tw-m-0 tw-mb-2 tw-text-center tw-text-sm tw-font-atkinson tw-text-neutral-600">
              Click “Apply filters” to update the dashboard
            </p>
          )}
          <Button
            onClick={applyFilters}
            label="Apply filters"
            outlined
            disabled={!hasPendingChanges}
            className="w-11 mx-auto block"
          />
        </div>
      }
      pt={{
        label: {
          className: 'py-1',
        },
      }}
    />
  );
};
